import React, { useEffect } from 'react';
import { AddressList, AddressListItem, AddressSharedDisclaimer, AddressSuggestionContainer, CustomTextButton, FooterContainer, InformationText, LogoAndPrivacyContainer, LogoChildContainer, PoweredByGoogleLogo, PrivacyLinkContainer, SuggestedAddressLine1 } from './StyledComponents';
import { getFormattedAddressText } from './utils';
import { ARROW_UP_DOWN_KEY_CODE, CHAR_COUNT_TO_HIDE_INFO_TEXT, LEGACY_KEYCODE_MAPPING, NAVIGATION_KEY_CODES } from './constants';

var AddressSuggestionDropdown = props => {
  var {
    addressSelectionHandler,
    contentObject,
    fieldsPrefix,
    inputId,
    inputName,
    suggestedAddressList,
    mouseHoverNavigationIndex,
    setMouseHoverNavigationIndex,
    setSuggestionContainer,
    userInputText,
    enableDontSeeMyAddressSuggestion,
    overrideAddressSuggestionStyle
  } = props;
  var [keyboardNavigationIndex, setKeyboardNavigationIndex] = React.useState(-1);
  var showInformationText = !suggestedAddressList.length && userInputText.length < CHAR_COUNT_TO_HIDE_INFO_TEXT;
  useEffect(() => {
    var newAddressIndex = keyboardNavigationIndex;

    var documentClickHandler = () => {
      if (document.activeElement.id !== inputId) {
        setSuggestionContainer(false);
      } else {
        setKeyboardNavigationIndex(-1);
        newAddressIndex = keyboardNavigationIndex;
      }
    };

    var isPrivacyLinkInFooterEnabled = contentObject.privacyLink && contentObject.privacyLinkText;

    var keyUpHandler = e => {
      var keyCode = e.code || LEGACY_KEYCODE_MAPPING[e.keyCode];

      if (!NAVIGATION_KEY_CODES.includes(keyCode) || !suggestedAddressList.length) {
        return;
      }

      var activeElementId = document.activeElement.id || '';

      if (isPrivacyLinkInFooterEnabled && activeElementId.includes('googlePrivacyLink')) {
        switch (keyCode) {
          case 'ArrowUp':
            newAddressIndex = suggestedAddressList.length - 1;
            e.preventDefault();
            break;

          case 'ArrowDown':
            e.preventDefault();
            break;

          case 'Tab':
            if (keyboardNavigationIndex > -1) {
              newAddressIndex = suggestedAddressList.length - 1;
            } else {
              newAddressIndex = 0;
            }

            break;

          default:
        }

        if (keyCode === 'ArrowUp') {
          setMouseHoverNavigationIndex(newAddressIndex);
          setKeyboardNavigationIndex(newAddressIndex);
          document.getElementById("addressIndex".concat(newAddressIndex)).focus();
        }
      }

      var setFocusToPrivacy = false;

      if (activeElementId.includes(inputId) || activeElementId.includes('addressIndex')) {
        switch (keyCode) {
          case 'ArrowUp':
            if (newAddressIndex > -1) {
              newAddressIndex--;
            }

            e.preventDefault();
            break;

          case 'ArrowDown':
            if (newAddressIndex < suggestedAddressList.length - 1) {
              newAddressIndex++;
            } else if (!isPrivacyLinkInFooterEnabled) {
              e.preventDefault();
            } else {
              setFocusToPrivacy = true;
            }

            break;

          case 'Tab':
            if (activeElementId.includes('addressIndex')) {
              newAddressIndex = activeElementId.slice(-1);
            } else if (keyboardNavigationIndex > -1) {
              newAddressIndex--;
            }

            break;

          default:
        }

        if (ARROW_UP_DOWN_KEY_CODE.includes(keyCode)) {
          if (newAddressIndex === -1) {
            document.getElementById(inputId).focus();
            setMouseHoverNavigationIndex(0);
          } else if (newAddressIndex < suggestedAddressList.length) {
            document.getElementById("addressIndex".concat(newAddressIndex)).focus();
            setMouseHoverNavigationIndex(newAddressIndex);
          }
        }

        if (setFocusToPrivacy && isPrivacyLinkInFooterEnabled) {
          setMouseHoverNavigationIndex(-1);
          document.getElementById('googlePrivacyLink').focus();
        } else {
          setKeyboardNavigationIndex(newAddressIndex);
        }
      } else if (!activeElementId.includes('googlePrivacyLink')) {
        setSuggestionContainer(false);
      }
    };

    var keyDownHandler = e => {
      var keyCode = e.code || LEGACY_KEYCODE_MAPPING[e.keyCode];

      if (ARROW_UP_DOWN_KEY_CODE.includes(keyCode)) {
        e.preventDefault();
      }
    };

    document.addEventListener('click', documentClickHandler);
    document.addEventListener('keyup', keyUpHandler);
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('click', documentClickHandler);
      document.removeEventListener('keyup', keyUpHandler);
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [suggestedAddressList.length, keyboardNavigationIndex]);

  var PrivacyLink = props => React.createElement(PrivacyLinkContainer, {
    "data-testid": "google-privacylink",
    overrideAddressSuggestionStyle: props === null || props === void 0 ? void 0 : props.overrideAddressSuggestionStyle
  }, React.createElement("a", {
    href: contentObject.privacyLink,
    id: "googlePrivacyLink",
    rel: "noopener noreferrer",
    target: "_blank"
  }, contentObject.privacyLinkText));

  var renderPoweredByGoogleLogoWithPrivacy = _ref => {
    var {
      props
    } = _ref;
    return React.createElement(LogoAndPrivacyContainer, {
      overrideAddressSuggestionStyle: overrideAddressSuggestionStyle
    }, React.createElement(LogoChildContainer, null, PrivacyLink({
      overrideAddressSuggestionStyle: props === null || props === void 0 ? void 0 : props.overrideAddressSuggestionStyle
    })), contentObject.privacyLink && React.createElement(LogoChildContainer, null, React.createElement(PoweredByGoogleLogo, {
      "data-testid": "google-logo",
      overrideAddressSuggestionStyle: overrideAddressSuggestionStyle
    })));
  };

  return React.createElement(AddressSuggestionContainer, {
    "data-dd-action-name": "[Auto-suggested address]",
    id: "addressSuggestionContainer"
  }, showInformationText && React.createElement(InformationText, {
    className: "infoText"
  }, contentObject.line1Instruction), React.createElement(AddressList, {
    id: "suggestedAddressList"
  }, suggestedAddressList.map((_ref2, index) => {
    var {
      mainText,
      placeId,
      secondaryText
    } = _ref2;
    var {
      line1MatchingUserInput,
      line1Rest
    } = getFormattedAddressText(mainText, userInputText);
    var highLightLastItem = enableDontSeeMyAddressSuggestion && index === suggestedAddressList.length - 1;
    return React.createElement(AddressListItem, {
      key: placeId
    }, React.createElement(CustomTextButton, {
      id: "addressIndex".concat(index),
      isActiveSuggestion: index === mouseHoverNavigationIndex,
      onClick: e => addressSelectionHandler(e, {
        addressType: fieldsPrefix,
        id: inputId,
        name: inputName,
        placeId
      }),
      onMouseEnter: () => setMouseHoverNavigationIndex(index),
      onMouseLeave: () => setMouseHoverNavigationIndex(null),
      overrideAddressSuggestionStyle: overrideAddressSuggestionStyle,
      reducedHeight: highLightLastItem
    }, React.createElement(SuggestedAddressLine1, {
      enableDontSeeMyAddressSuggestion: highLightLastItem,
      overrideAddressSuggestionStyle: overrideAddressSuggestionStyle
    }, overrideAddressSuggestionStyle && highLightLastItem && (!mainText.startsWith(userInputText) || !userInputText) && React.createElement(React.Fragment, null, React.createElement("strong", null, mainText)), overrideAddressSuggestionStyle && !highLightLastItem && React.createElement(React.Fragment, null, React.createElement("strong", null, line1MatchingUserInput), React.createElement("b", null, line1Rest)), !overrideAddressSuggestionStyle && React.createElement(React.Fragment, null, line1MatchingUserInput, React.createElement("b", null, line1Rest))), secondaryText));
  })), !showInformationText && Boolean(suggestedAddressList.length) && React.createElement(FooterContainer, {
    overrideAddressSuggestionStyle: overrideAddressSuggestionStyle
  }, contentObject.privacyLink && renderPoweredByGoogleLogoWithPrivacy({
    overrideAddressSuggestionStyle
  }), !contentObject.privacyLink && React.createElement(PoweredByGoogleLogo, {
    "data-testid": "google-logo",
    overrideAddressSuggestionStyle: overrideAddressSuggestionStyle
  }), contentObject.addressSharedDisclaimer && React.createElement(AddressSharedDisclaimer, null, contentObject.addressSharedDisclaimer)));
};

AddressSuggestionDropdown.defaultProps = {
  suggestedAddressList: [],
  userInputText: ''
};
export { AddressSuggestionDropdown };